<!--
  - Copyright (c) 2008-2025 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
  - This is CONFIDENTIAL code. Use is subject to license terms.
  -->
<template>
  <div :style="isMSIE() ? 'overflow-y: hidden' : ''">
    <div v-if="isMSIE()" class="alert alert-warning w-75 mx-auto" style="margin-top: 48px">
      <div class="d-flex">
        <icon :icon="['fas', 'exclamation-triangle']" size="3x" class="ie-warning"></icon>
        <span class="ml-3" v-html="$t('msieNotSupported')"></span>
      </div>
    </div>

    <router-view v-if="!isMSIE()"></router-view>

    <!-- Global Upgrade Modal -->
    <modal
      v-model="upgradeModal"
      :title="$t('upgradeConfirmTitle')"
      :ok-text="upgradeModalOkText"
      ok-class="btn-success"
      @ok="upgradeModalOk"
    >
      <div class="text-dark">{{ upgradeModalText }}<br /></div>
    </modal>

    <!-- Global Inquiry Modal -->
    <modal
      v-model="inquiryModal"
      :title="$t('inquireTitle')"
      :ok-text="$t('confirms.inquire')"
      ok-class="btn-success"
      @ok="forwardToInquiryForm()"
    >
      <div class="text-dark">
        {{ $t("inquireText") }}<br /><br />
        {{ $t("contact.phone") }}: <a :href="telLink(phone)">{{ phone }}</a
        ><br />
        {{ $t("contact.email") }}: <a :href="mailtoLink(email)">{{ email }}</a
        ><br />
        {{ $t("contact.website") }}: <a :href="httpsLink(inquiryForm)" target="_blank">{{ inquiryForm }}</a
        ><br />
      </div>
    </modal>
  </div>
</template>

<script>
import Modal from "@/components/Modal"
import LinksMixin from "@/utils/LinksMixin"
import { EventBus } from "~/common/src/event-bus"
import { LICENSING_UPGRADE_REQUIRED, FEATURE_UNDER_DEVELOPMENT } from "@/config/events"
import { COMPANY_ONLINE_SHOP, COMPANY_PHONE, COMPANY_EMAIL, COMPANY_INQUIRY_FORM } from "@/config/company"
import { detectIE } from "@/utils/IEDetection"

export default {
  name: "App",

  components: { Modal },

  mixins: [LinksMixin],

  data() {
    return {
      upgradeModal: false,
      inquiryModal: false,
      phone: COMPANY_PHONE,
      email: COMPANY_EMAIL,
      inquiryForm: COMPANY_INQUIRY_FORM,
    }
  },

  created() {
    // load the system config (used for determining whether to display or hide specific features)
    this.$store.dispatch("settings/loadSystemConfiguration")
    this.$store.dispatch("settings/loadMaintenanceMessage")

    // register global event handlers for displaying the upgrade and inquiry modals
    EventBus.$on(LICENSING_UPGRADE_REQUIRED, this.showUpgradeModal)
    EventBus.$on(FEATURE_UNDER_DEVELOPMENT, this.showInquiryModal)

    // Important note: This line may look harmless, but it's not. The tenantFilterId works as follows:
    //  1) HERE it is set to whatever is stored in the local storage (for non-tenant users)
    //  2) in user.js, the tenantId is then overwritten IF we're logged in as a tenant user
    //  The order is important. DON'T move this line to anywhere that's executed *after* loading the user, otherwise
    //  you're in a world of hurt.
    // Pending: Refactor this whole mechanism to a more stable one where ALL this information is present
    //   *before* the app is actually started
    this.$store.commit("tenants/setTenantFilterId", { loadFromWebStorage: true })
  },

  computed: {
    isCurrentUserGlobal() {
      let currentUser = this.$store.getters["user/userSettings"]

      return !currentUser.tenantId
    },

    upgradeModalText() {
      return this.isCurrentUserGlobal ? this.$t("upgradeConfirmTextGlobal") : this.$t("upgradeConfirmText")
    },

    upgradeModalOkText() {
      return this.isCurrentUserGlobal ? this.$t("confirms.ok") : this.$t("confirms.upgrade")
    },
  },

  methods: {
    isMSIE() {
      let ieVersion = detectIE()
      return ieVersion && ieVersion < 12
    },

    showUpgradeModal() {
      this.upgradeModal = true
    },

    showInquiryModal() {
      this.inquiryModal = true
    },

    upgradeModalOk() {
      if (!this.isCurrentUserGlobal) window.open(this.httpsLink(COMPANY_ONLINE_SHOP), "_blank")
    },

    forwardToInquiryForm() {
      window.open(this.httpsLink(COMPANY_INQUIRY_FORM), "_blank")
    },
  },

  i18n: {
    messages: {
      en: {
        msieNotSupported:
          "Please note that the Meeting Control Center does not support Internet Explorer.<br>We recommend you to " +
          "use the latest versions of Microsoft Edge, Google Chrome or Mozilla Firefox.",
        upgradeConfirmTitle: "Upgrade required",
        upgradeConfirmText:
          "This feature is not included in your current plan. Please visit our online store to upgrade.",
        upgradeConfirmTextGlobal:
          "This feature is not included in the current tenant's license plan. Navigate to the Tenant Administration page to upgrade this tenant's plan.",
        inquireTitle: "Currently under development",
        inquireText:
          "This feature is currently under development! You can already use it in a custom setup. Please get in touch and we are more than happy to set it up for you.",
      },
      de: {
        msieNotSupported:
          "Bitte beachten Sie, dass das Meeting Control Center den Internet Explorer nicht unterstützt.<br>Wir empfehlen " +
          "Ihnen, die neuesten Versionen von Microsoft Edge, Google Chrome oder Mozilla Firefox zu verwenden.",
        upgradeConfirmTitle: "Upgrade notwendig",
        upgradeConfirmText:
          "Diese Funktion ist in Ihrem Paket nicht enthalten. Besuchen Sie unseren Onlineshop, um sie freizuschalten.",
        upgradeConfirmTextGlobal:
          "Diese Funktion ist im Lizenzplan des aktuellen Mandanten nicht enthalten. Navigieren Sie zur Seite Mandanten-Verwaltung, um den Lizenzplan für den Mandanten zu aktualisieren.",
        inquireTitle: "In Entwicklung",
        inquireText:
          "Diese Funktion ist aktuell in Entwicklung. Sie können die Funktion bereits jetzt nutzen, wenn Sie uns erlauben, die Funktion für Sie einzurichten. Bitte nehmen Sie hierzu Kontakt mit uns auf. Wir würden uns sehr freuen, die Sache für Sie im Einzelfall möglich zu machen.",
      },
    },
  },
}
</script>

<style scoped lang="scss">
.ie-warning {
  min-width: 38px;
  max-width: 38px;
}
</style>
